import React from 'react';
import { Section, Button, FadeIn, SEO } from '../components/UI';
import { Heart, Shield, Users, Sparkles } from 'lucide-react';

export default function Dodsdoula() {
  return (
    <div className="pt-20">
      <SEO 
        title="Dødsdoula & Livets Afslutning" 
        description="En dødsdoula yder ikke-medicinsk støtte, omsorg og vejledning til den døende og de pårørende før, under og efter dødsfaldet. Få hjælp til livets afslutning."
      />
      
      {/* Hero Section */}
      <Section className="bg-transparent pt-8 md:pt-12 pb-12 md:pb-24 relative overflow-hidden">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center relative z-10">
          <div className="lg:col-span-5">
            <FadeIn className="pt-12 md:pt-0">
              <span className="text-sorg-earth font-medium tracking-[0.2em] uppercase text-xs mb-6 block">Støtte & Omsorg</span>
              <h1 className="text-4xl md:text-7xl font-serif text-sorg-dark mb-8 leading-[1.1]">
                Dødsdoula
              </h1>
              <p className="text-xl text-sorg-dark/70 leading-relaxed mb-10">
                Som dødsdoula er jeg din følgesvend gennem livets sidste rejse. Jeg tilbyder nærvær, praktisk hjælp og følelsesmæssig støtte til både dig og dine kære, så vi sammen kan skabe en værdig og fredfyldt afsked.
              </p>
              <div className="flex flex-wrap gap-4">
                <Button to="/kontakt" className="px-6 py-2.5 md:px-8 md:py-3">Kontakt mig</Button>
                <Button to="#hvad-er-en-doula" variant="secondary" className="px-6 py-2.5 md:px-8 md:py-3">Læs mere</Button>
              </div>
            </FadeIn>
          </div>
          
          <div className="lg:col-span-7">
            <FadeIn direction="left" delay={0.2}>
              <div className="relative">
                <div className="absolute -inset-4 bg-sorg-beige/20 rounded-[2rem] blur-2xl -z-10 transform rotate-2"></div>
                <div className="aspect-[4/3] md:aspect-[16/10] w-full overflow-hidden rounded-[2rem] soft-shadow-lg border border-white/50">
                  <img 
                    src="https://assets.auxodev.dk/assets/88d34a44-1e08-49cc-ba2b-c330133a247e/07d65433-fdfa-4751-b78d-6867f8789661.jpg" 
                    alt="Dødsdoula - Hænder der holder hinanden" 
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-1000"
                  />
                </div>
                <div className="absolute -bottom-6 md:left-0 lg:-left-6 bg-white/80 backdrop-blur-md p-6 rounded-2xl soft-shadow border border-white/50 max-w-[200px] hidden md:block">
                  <p className="text-sm font-serif text-sorg-dark italic">"Ingen bør gå den sidste vej alene eller i utryghed."</p>
                </div>
              </div>
            </FadeIn>
          </div>
        </div>
      </Section>

      {/* Content Section - Hvad gør en dødsdoula */}
      <Section id="hvad-er-en-doula" glass>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-16 relative z-10">
          <div className="lg:col-span-2 space-y-8 text-sorg-dark/70 text-lg leading-relaxed">
            <FadeIn>
              <h2 className="text-3xl font-serif text-sorg-dark mb-6">Hvad gør en dødsdoula?</h2>
              <p>
                En dødsdoula er en ikke-medicinsk støtteperson, der komplementerer det sundhedsfaglige personale. Hvor læger og sygeplejersker fokuserer på den fysiske pleje og behandling, fokuserer jeg på det hele menneske – det emotionelle, det spirituelle og det praktiske.
              </p>
              <p>
                Min opgave er at holde rummet. At skabe en atmosfære af tryghed, hvor døden ikke er noget, vi gemmer væk, men noget vi møder med åbenhed og værdighed. Jeg hjælper med at navigere i de mange følelser og valg, der opstår, når livet rinder ud.
              </p>
            </FadeIn>

            <FadeIn delay={0.2}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-12">
                <div className="p-8 bg-white/40 rounded-3xl border border-white/60">
                  <h3 className="text-xl font-serif text-sorg-dark mb-4">Praktisk støtte</h3>
                  <p className="text-sm mb-4">Hjælp til at organisere hverdagen, koordinere besøg og skabe et roligt miljø i hjemmet eller på hospitalet.</p>
                </div>
                <div className="p-8 bg-white/40 rounded-3xl border border-white/60">
                  <h3 className="text-xl font-serif text-sorg-dark mb-4">Følelsesmæssig støtte</h3>
                  <p className="text-sm mb-4">Samtaler om frygt, håb og eftermæle. Støtte til at sige farvel og finde fred med relationer.</p>
                </div>
                <div className="p-8 bg-white/40 rounded-3xl border border-white/60">
                  <h3 className="text-xl font-serif text-sorg-dark mb-4">Våge-timer</h3>
                  <p className="text-sm mb-4">Nærvær ved dødslejet, så den døende aldrig er alene, og de pårørende kan få et hvil.</p>
                </div>
                <div className="p-8 bg-white/40 rounded-3xl border border-white/60">
                  <h3 className="text-xl font-serif text-sorg-dark mb-4">Efter dødsfaldet</h3>
                  <p className="text-sm mb-4">Hjælp til de første timer efter døden, støtte til ritualer og indledende sorgvejledning for de efterladte.</p>
                </div>
              </div>
            </FadeIn>
          </div>

          <div className="lg:col-span-1">
            <FadeIn direction="left" className="bg-sorg-beige/20 p-10 rounded-3xl border border-sorg-beige sticky top-32">
              <h3 className="text-2xl font-serif text-sorg-dark mb-6 text-center">En tryg hånd</h3>
              <p className="text-sm text-sorg-dark/60 mb-8 text-center">
                Ønsker du at høre mere om, hvordan en dødsdoula kan støtte dig eller din familie?
              </p>
              <Button to="/kontakt" className="w-full px-6 py-2.5 md:px-8 md:py-3">Aftal en samtale</Button>
            </FadeIn>
          </div>
        </div>
      </Section>

      {/* New Section: End of Life-forløb (Smeltet sammen her) */}
      <Section id="forloeb" className="bg-sorg-sand/20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center relative z-10">
          <div className="lg:col-span-7 order-2 lg:order-1">
            <FadeIn direction="right">
              <div className="relative">
                <div className="absolute -inset-4 bg-sorg-beige/20 rounded-[2rem] blur-2xl -z-10 transform -rotate-2"></div>
                <div className="aspect-[4/3] md:aspect-[16/10] w-full overflow-hidden rounded-[2rem] soft-shadow-lg border border-white/50">
                  <img 
                    src="https://assets.auxodev.dk/assets/88d34a44-1e08-49cc-ba2b-c330133a247e/78701a5a-1a43-4435-830b-8179f9568599.jpg" 
                    alt="Livets Afslutning - Fredfyldt solnedgang" 
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-1000"
                  />
                </div>
                <div className="absolute -bottom-6 md:right-0 lg:-right-6 bg-white/80 backdrop-blur-md p-6 rounded-2xl soft-shadow border border-white/50 max-w-[200px] hidden md:block">
                  <p className="text-sm font-serif text-sorg-dark italic">"En værdig afsked skaber fred for dem, der bliver tilbage."</p>
                </div>
              </div>
            </FadeIn>
          </div>

          <div className="lg:col-span-5 order-1 lg:order-2 space-y-6">
            <FadeIn>
              <span className="text-sorg-earth font-medium tracking-[0.2em] uppercase text-xs mb-4 block">Forløb ved livets afslutning</span>
              <h2 className="text-3xl md:text-4xl font-serif text-sorg-dark mb-6">Støtte i den sidste tid</h2>
              <p className="text-sorg-dark/70 text-lg leading-relaxed mb-6">
                Når livet går mod sin afslutning, opstår der ofte mange spørgsmål, følelser og praktiske overvejelser. Som vejleder og dødsdoula støtter jeg både den døende og de pårørende i at navigere i denne svære tid.
              </p>
              <p className="text-sorg-dark/70 text-lg leading-relaxed mb-8">
                Mit fokus er at skabe tryghed og nærvær. Det kan handle om de svære samtaler, om at finde fred med det liv, der er levet, eller om at sikre, at de sidste ønsker bliver hørt og respekteret. Jeg tilbyder et lyttende øre og en rolig hånd i en tid, hvor alt kan føles kaotisk.
              </p>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mt-8">
                <div className="p-6 bg-white/60 rounded-2xl border border-white/80">
                  <h3 className="text-lg font-serif text-sorg-dark mb-3">For den døende</h3>
                  <ul className="space-y-2 text-sm text-sorg-dark/70">
                    <li className="flex items-center space-x-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-sorg-earth"></div>
                      <span>Eksistentielle samtaler</span>
                    </li>
                    <li className="flex items-center space-x-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-sorg-earth"></div>
                      <span>Hjælp til sidste ønsker</span>
                    </li>
                    <li className="flex items-center space-x-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-sorg-earth"></div>
                      <span>Nærvær og vågenat</span>
                    </li>
                  </ul>
                </div>
                <div className="p-6 bg-white/60 rounded-2xl border border-white/80">
                  <h3 className="text-lg font-serif text-sorg-dark mb-3">For de pårørende</h3>
                  <ul className="space-y-2 text-sm text-sorg-dark/70">
                    <li className="flex items-center space-x-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-sorg-earth"></div>
                      <span>Aflastning og støtte</span>
                    </li>
                    <li className="flex items-center space-x-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-sorg-earth"></div>
                      <span>Vejledning i sorgprocessen</span>
                    </li>
                    <li className="flex items-center space-x-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-sorg-earth"></div>
                      <span>Praktisk & mental forberedelse</span>
                    </li>
                  </ul>
                </div>
              </div>
            </FadeIn>
          </div>
        </div>
      </Section>

      {/* Features Section */}
      <Section className="bg-sorg-sand/40 backdrop-blur-sm">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 text-center">
            <FadeIn delay={0.1}>
              <div className="w-14 h-14 bg-white rounded-full flex items-center justify-center text-sorg-earth mx-auto mb-4 soft-shadow">
                <Shield size={24} strokeWidth={1} />
              </div>
              <h4 className="font-serif text-lg mb-2">Tryghed</h4>
              <p className="text-xs text-sorg-dark/60">En stabil støtte i en uforudsigelig tid.</p>
            </FadeIn>
            <FadeIn delay={0.2}>
              <div className="w-14 h-14 bg-white rounded-full flex items-center justify-center text-sorg-earth mx-auto mb-4 soft-shadow">
                <Users size={24} strokeWidth={1} />
              </div>
              <h4 className="font-serif text-lg mb-2">Fællesskab</h4>
              <p className="text-xs text-sorg-dark/60">Jeg hjælper med at samle familien om den svære afsked.</p>
            </FadeIn>
            <FadeIn delay={0.3}>
              <div className="w-14 h-14 bg-white rounded-full flex items-center justify-center text-sorg-earth mx-auto mb-4 soft-shadow">
                <Heart size={24} strokeWidth={1} />
              </div>
              <h4 className="font-serif text-lg mb-2">Omsorg</h4>
              <p className="text-xs text-sorg-dark/60">Hjertet er med i alt, hvad jeg gør.</p>
            </FadeIn>
            <FadeIn delay={0.4}>
              <div className="w-14 h-14 bg-white rounded-full flex items-center justify-center text-sorg-earth mx-auto mb-4 soft-shadow">
                <Sparkles size={24} strokeWidth={1} />
              </div>
              <h4 className="font-serif text-lg mb-2">Værdighed</h4>
              <p className="text-xs text-sorg-dark/60">Jeg ærer den døendes ønsker og liv.</p>
            </FadeIn>
          </div>
        </div>
      </Section>
    </div>
  );
}
